import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Bot, User as UserIcon, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STORAGE_KEY = "togc_support_session";
const WELCOME = {
  role: "assistant",
  content: "¡Hola! Soy TestOpoBot 👮‍♂️. Pregúntame cualquier duda sobre la plataforma, el plan Premium, fechas del BOE, simulacros o cómo prepararte. ¿En qué te ayudo?",
};

export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [showContactCTA, setShowContactCTA] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", {
        session_id: sessionId,
        message: text,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setShowContactCTA(!!data.open_contact_form);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: formatApiError(err.response?.data?.detail) || "Estoy teniendo problemas para responder. Inténtalo en unos segundos." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const SUGGESTIONS = [
    "¿Qué incluye Premium?",
    "¿Cuántas plazas hay?",
    "¿Cómo funciona el reto diario?",
    "¿Cuándo es el examen?",
  ];

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-gcgreen to-gcgreen-dark border border-gcgold/40 shadow-[0_10px_40px_-5px_rgba(10,92,54,0.6)] hover:shadow-[0_15px_50px_-5px_rgba(212,175,55,0.5)] hover:scale-105 transition-all flex items-center justify-center group"
        aria-label={open ? "Cerrar chat" : "Abrir chat de soporte"}
        data-testid="support-chat-toggle"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6 text-white" />
            </motion.div>
          ) : (
            <motion.div key="msg" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} className="relative">
              <MessageCircle className="h-6 w-6 text-white" />
              {/* Pulse */}
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gcgold opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-gcgold" />
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-[400px] h-[600px] max-h-[80vh] bg-ink-800 border border-white/10 rounded-2xl shadow-[0_30px_80px_-10px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden backdrop-blur-xl"
            data-testid="support-chat-panel"
          >
            {/* Header */}
            <div className="px-4 py-3.5 border-b border-white/10 bg-gradient-to-r from-gcgreen/30 via-ink-800 to-ink-800 flex items-center gap-3">
              <div className="relative h-10 w-10 rounded-xl bg-gcgreen/30 flex items-center justify-center flex-shrink-0">
                <Bot className="h-5 w-5 text-gcgold" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-ink-800" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white text-sm">TestOpoBot</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-mono">En línea · IA</div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="support-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    m.role === "user" ? "bg-gcgreen/30" : "bg-gcgold/15"
                  }`}>
                    {m.role === "user" ? <UserIcon className="h-3.5 w-3.5 text-gcgreen-light" /> : <Bot className="h-3.5 w-3.5 text-gcgold" />}
                  </div>
                  <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gcgreen text-white rounded-tr-sm"
                      : "bg-white/[0.04] border border-white/5 text-slate-200 rounded-tl-sm"
                  }`}>
                    {m.content.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
                      part.startsWith("**") && part.endsWith("**")
                        ? <strong key={idx} className={m.role === "user" ? "text-white" : "text-white"}>{part.slice(2, -2)}</strong>
                        : <span key={idx}>{part}</span>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex items-start gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gcgold/15 flex items-center justify-center"><Bot className="h-3.5 w-3.5 text-gcgold" /></div>
                  <div className="px-3.5 py-3 rounded-2xl bg-white/[0.04] border border-white/5">
                    <div className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-gcgold animate-pulse" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gcgold animate-pulse" style={{ animationDelay: "0.2s" }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-gcgold animate-pulse" style={{ animationDelay: "0.4s" }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Contact form CTA */}
              {showContactCTA && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="ml-9">
                  <Link
                    to={user ? `/contacto?email=${encodeURIComponent(user.email)}&name=${encodeURIComponent(user.name || "")}` : "/contacto"}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gcgold text-ink text-xs font-bold hover:bg-gcgold-light transition-colors"
                    data-testid="support-open-contact"
                  >
                    Abrir formulario de contacto <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </motion.div>
              )}

              {/* Suggestions on first load */}
              {messages.length === 1 && (
                <div className="pt-3 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); setTimeout(() => inputRef.current?.focus(), 0); }}
                      className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-colors"
                      data-testid="support-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={send} className="border-t border-white/10 px-3 py-3 flex items-center gap-2 bg-ink-900/50">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu pregunta..."
                disabled={sending}
                maxLength={2000}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-gcgold/40 focus:bg-white/[0.06] transition-colors disabled:opacity-50"
                data-testid="support-chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="h-10 w-10 rounded-xl bg-gcgold hover:bg-gcgold-light text-ink flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                data-testid="support-chat-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
